'use client'

import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import type { ImageProps } from 'next/image'
import Image from 'next/image'
import { rgba } from 'polished'
import { useState } from 'react'
import { useChainColor } from '@/src/hooks/use-chain-color'
import { cn } from '@/src/utils/styles'

const chainImgVariants = cva(
  'flex items-center justify-center size-6 rounded-[6px]',
  {
    variants: {
      size: {
        sm: 'size-4 rounded-[6px]',
        md: 'size-6.5 rounded-[6px]',
        default: 'size-6 rounded-[6px]',
        lg: 'size-12 rounded-xl',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

const IMAGE_SIZES = {
  sm: 12,
  default: 16,
  md: 16,
  lg: 30,
}

interface ChainImgProps
  extends Omit<ImageProps, 'src' | 'alt'>,
    VariantProps<typeof chainImgVariants> {
  chainName?: string
  chainImg?: string
  chainId: string
  containerClassName?: string
  premultiply?: boolean
}

export const ChainImg = ({
  chainName,
  chainImg,
  chainId,
  containerClassName,
  size,
  premultiply = false,
  ...props
}: ChainImgProps) => {
  const [imageError, setImageError] = useState(false)

  const { chainColor } = useChainColor(chainId)

  if (!chainImg && !chainName) return null

  let backgroundColor = chainColor ? rgba(chainColor, 0.3) : undefined
  if (premultiply && backgroundColor) {
    backgroundColor = `linear-gradient(${backgroundColor}, ${backgroundColor}), rgba(var(--gray-900))`
  }

  return (
    <div
      className={cn(
        chainImgVariants({ size: size, className: containerClassName }),
        !chainColor && 'bg-surface1',
      )}
      style={{
        background: backgroundColor,
      }}
    >
      {imageError || !chainImg ? (
        <span className='text-xs text-secondary font-medium'>
          {chainName ? getInitials(chainName) : '??'}
        </span>
      ) : (
        <Image
          src={chainImg}
          alt={`${chainName} image`}
          width={IMAGE_SIZES[size || 'default']}
          height={IMAGE_SIZES[size || 'default']}
          onError={() => setImageError(true)}
          {...props}
        />
      )}
    </div>
  )
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
}
